import * as React from 'react';

import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';

import {appHistory} from '../../../../history';
import {getDefaultStagesetId} from '../../../../util/get-default-stageset-id';

import {useGetStagesets} from '../../select/hooks/use-get-stagesets';
import {useGetSessionUser} from '../../use-get-session-user';

import {AUTO_DISTRIBUTE_OPTION} from './consts';

interface Props {
    inputId?: string;
    selectedPipeline: string | null | undefined;
    onSelectPipeline: (arg1: string) => void;
    minHeight?: number;
    menuPlacement?: 'auto' | 'top' | 'bottom';
    // Only use this if you wanna include the option but not auto select it
    useAutoDistribute?: boolean;
    // If true, automatically selects the auto-distribute option and enables useAutoDistribute.
    // WARNING: If both autoSelectAutoDistribute and autoSelectDefault are true,
    // autoSelectAutoDistribute will take precedence.
    autoSelectAutoDistribute?: boolean;
    autoselectDefault?: boolean;
    isDisabled?: boolean;
}

export function PipelineSelectPicker(props: Props) {
    const {stagesets, isLoading} = useGetStagesets();
    const {selectedPipeline, onSelectPipeline, autoselectDefault, autoSelectAutoDistribute} = props;
    const {user} = useGetSessionUser();

    // autoSelectAutoDistribute automatically enables useAutoDistribute
    const useAutoDistribute = props.useAutoDistribute || autoSelectAutoDistribute;

    const options = stagesets.map((stageset) => {
        return {
            isDisabled: !stageset.canAccess,
            label: stageset.name,
            value: stageset.id,
        };
    });

    if (useAutoDistribute) {
        // @ts-expect-error
        options.unshift({...AUTO_DISTRIBUTE_OPTION, isDisabled: false});
    }

    const value = options.find((opt) => opt.value === selectedPipeline);
    const defaultPipeline = options.find((opt) => opt.value === getDefaultStagesetId());
    const autoDistributeOption = useAutoDistribute
        ? options.find((opt) => opt.value === AUTO_DISTRIBUTE_OPTION.value)
        : null;

    const onChange = React.useCallback(
        (option) => {
            if (option && !Array.isArray(option)) {
                onSelectPipeline(option.value);
            }
        },
        [onSelectPipeline]
    );

    // Track whether auto-selection has already been attempted to prevent
    // triggering onChange multiple times as dependencies change during data loading
    const hasAttemptedAutoSelectRef = React.useRef<boolean>(false);

    // Track the initial selectedPipeline to know if we should auto-select
    // If the parent passed a value, we should never auto-select
    const initialSelectedPipelineRef = React.useRef<string | null | undefined>(selectedPipeline);

    React.useEffect(() => {
        // Don't auto-select if:
        // 1. We've already attempted auto-selection
        // 2. There's already a value selected
        // 3. The parent component initially passed a selectedPipeline value
        if (hasAttemptedAutoSelectRef.current || value || initialSelectedPipelineRef.current) {
            return;
        }

        if (autoSelectAutoDistribute && autoDistributeOption) {
            hasAttemptedAutoSelectRef.current = true;
            onChange(autoDistributeOption);
        } else if (autoselectDefault && defaultPipeline) {
            hasAttemptedAutoSelectRef.current = true;
            onChange(defaultPipeline);
        }
    }, [
        autoSelectAutoDistribute,
        autoDistributeOption,
        autoselectDefault,
        defaultPipeline,
        value,
        onChange,
    ]);

    return (
        <SelectPickerGeneric
            inputId={props.inputId}
            minHeight={props.minHeight}
            placeholder='Select a pipeline…'
            isLoading={isLoading}
            autoFocus={false}
            openMenuOnFocus={true}
            options={options}
            onChange={onChange}
            value={value}
            menuPlacement={props.menuPlacement}
            isDisabled={props.isDisabled}
            footerButton={
                user && user.permissions.canAccessSetup
                    ? {
                          label: 'Manage pipelines',
                          onClick: () => {
                              appHistory.push('/sales/pipelines');
                          },
                      }
                    : undefined
            }
        />
    );
}
